import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import DataTable from '../../components/DataTable';
import BulkActions from '../../components/BulkActions';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  UserCheck, 
  Mail, 
  Phone, 
  MapPin,
  Eye,
  Pencil,
  Check,
  Home,
  Users,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const Parents = ({ mode }) => {
  const isCustomers = mode === 'customers';
  const pageTitle = isCustomers ? 'Customers' : 'Parents';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParent, setSelectedParent] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([]);
  
  // Bulk selection state
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('active');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchParents = async () => {
    try {
      const response = await api.get('/parents', { params: isCustomers ? { role: 'customer' } : { view: 'directory' } });
      setParents(response.data);
    } catch (error) {
      toast.error('Failed to fetch parents');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/email/templates');
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  useEffect(() => {
    fetchParents();
    fetchEmailTemplates();
  }, []);

  // Bulk selection handlers
  const _handleSelectAll = (checked) => {
    if (checked) {
      setSelectedParentIds(parents.map(p => p.id));
    } else {
      setSelectedParentIds([]);
    }
  };

  const _handleSelectParent = (parentId, checked) => {
    if (checked) {
      setSelectedParentIds([...selectedParentIds, parentId]);
    } else {
      setSelectedParentIds(selectedParentIds.filter(id => id !== parentId));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedParentIds.length === 0) {
      toast.error('No parents selected');
      return;
    }

    setBulkProcessing(true);
    try {
      // iter258 — send `is_active` (bool) instead of the legacy
      // `status` string. Backend `bulk-status` endpoints accept both.
      const response = await api.put('/admin/parents/bulk-status', {
        parent_ids: selectedParentIds,
        is_active: bulkStatus === 'active',
        status: bulkStatus,  // kept for backward-compat with older backend
      });
      
      toast.success(`Updated ${response.data.updated} parents to ${bulkStatus}`);
      setShowBulkStatusDialog(false);
      setSelectedParentIds([]);
      fetchParents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkSendEmail = async () => {
    if (selectedParentIds.length === 0) {
      toast.error('No parents selected');
      return;
    }
    if (!selectedTemplateId) {
      toast.error('Please select an email template');
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await api.post('/admin/parents/bulk-email', {
        parent_ids: selectedParentIds,
        template_id: selectedTemplateId
      });
      
      toast.success(`Email sent to ${response.data.sent} parents`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} emails failed to send`);
      }
      setShowBulkEmailDialog(false);
      setSelectedParentIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSendLoginCredentials = async () => {
    if (selectedParentIds.length === 0) {
      toast.error('No parents selected');
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await api.post('/admin/users/send-login-emails', selectedParentIds);
      
      toast.success(`Login credentials sent to ${response.data.sent} parents`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} emails failed`);
      }
      setSelectedParentIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send login credentials');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Handle highlight parameter from URL (e.g., when navigating from player profile)
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && parents.length > 0) {
      const parentToHighlight = parents.find(p => p.id === highlightId);
      if (parentToHighlight) {
        // Navigate to the parent's detail page
        navigate(`/admin/parents/${highlightId}`);
        // Clear the highlight param
        searchParams.delete('highlight');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [parents, searchParams, navigate]);

  const openEditDialog = (parent) => {
    setSelectedParent(parent);
    setEditData({
      name: parent.name || '',
      email: parent.email || '',
      phone: parent.phone || '',
      address: parent.profile?.address || '',
      city: parent.profile?.city || '',
      state: parent.profile?.state || '',
      zip: parent.profile?.zip || '',
      emergency_contact_name: parent.profile?.emergency_contact_name || '',
      emergency_contact_phone: parent.profile?.emergency_contact_phone || '',
      notes: parent.profile?.admin_notes || ''
    });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/parents/${selectedParent.id}`, editData);
      toast.success(`Parent profile updated for ${editData.name}`);
      setEditDialog(false);
      fetchParents();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update parent profile');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Parent',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">
              {row.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(isCustomers ? `/admin/customers/${row.id}` : `/admin/parents/${row.id}`)}
                className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
                data-testid={`parent-name-link-${row.id}`}
              >
                {row.name}
              </button>
              {row.numeric_id && (
                <span className="text-xs text-slate-400 font-mono">ID: {row.numeric_id}</span>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Mail className="h-3 w-3" />
          {row.email}
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Phone className="h-3 w-3" />
          {row.phone}
        </div>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'profile.city',
      label: 'Location',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.profile?.city ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <MapPin className="h-3 w-3" />
          {row.profile.city}{row.profile.state ? `, ${row.profile.state}` : ''}
        </div>
      ) : <span className="text-slate-400">Not provided</span>
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      // iter258 — read `is_active` (bool). `status` field is being
      // phased out; backend still keeps them in lockstep, but new UI
      // reads the canonical flag.
      render: (row) => (
        <Badge className={row.is_active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
          {row.is_active === false ? 'Inactive' : 'Active'}
        </Badge>
      )
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    }
  ];

  const actions = (row) => (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(isCustomers ? `/admin/customers/${row.id}` : `/admin/parents/${row.id}`)}
        data-testid={`view-parent-${row.id}`}
      >
        <Eye className="h-4 w-4 mr-1" />
        View
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => openEditDialog(row)}
        data-testid={`edit-parent-${row.id}`}
      >
        <Pencil className="h-4 w-4 mr-1" />
        Edit
      </Button>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title={pageTitle}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={pageTitle}>
      <div className="space-y-6" data-testid="parents-page">
        <div className="space-y-3">
          <p className="text-slate-600">{isCustomers ? 'Lane booking customers' : 'View registered parents'}</p>
          <div className="flex flex-wrap items-center gap-3">
            <ColumnConfig
              columns={columns.filter(c => c.key !== 'select')}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="admin-parents-columns"
              requiredColumns={['name']}
            />
            <BulkActions
              entityName="Parents"
              exportEndpoint="/admin/parents/export"
              uploadEndpoint="/admin/parents/bulk-upload"
              onUploadComplete={fetchParents}
              sampleFields={['name', 'email', 'phone']}
              showWelcomeEmailOption={true}
            />
            <div className="text-sm text-slate-500">
              Total: {parents.length} parents
            </div>
          </div>
        </div>

        {/* Bulk Action Bar - shows when parents are selected */}
        {selectedParentIds.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {selectedParentIds.length} parent{selectedParentIds.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkStatusDialog(true)}
                    data-testid="bulk-status-btn"
                    className="bg-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Update Status
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkEmailDialog(true)}
                    data-testid="bulk-email-btn"
                    className="bg-white"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Send Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSendLoginCredentials}
                    disabled={bulkProcessing}
                    data-testid="bulk-login-credentials-btn"
                    className="bg-white"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {bulkProcessing ? 'Sending...' : 'Send Login Credentials'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedParentIds([])}
                    data-testid="clear-selection-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <DataTable
              data={parents}
              columns={columns}
              visibleColumns={visibleColumns.length > 0 ? visibleColumns : []}
              actions={actions}
              emptyIcon={UserCheck}
              emptyMessage="No parents registered yet."
              searchable={true}
              searchFields={['name', 'email', 'phone', 'profile.city']}
              testIdPrefix="parents"
              defaultFilters={{ is_active: 'true' }}
              selectable={true}
              selectedRows={selectedParentIds}
              onSelectionChange={setSelectedParentIds}
              rowIdKey="id"
            />
          </CardContent>
        </Card>

        {/* Edit Parent Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-500" />
                Edit Parent Profile
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-blue-500" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Full Name *</Label>
                    <Input
                      id="edit-name"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      placeholder="Enter full name"
                      data-testid="edit-parent-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email *</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      placeholder="Enter email"
                      data-testid="edit-parent-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    data-testid="edit-parent-phone"
                  />
                </div>
              </div>

              {/* Address Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Home className="h-4 w-4 text-green-500" />
                  Address
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Street Address</Label>
                  <Input
                    id="edit-address"
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    placeholder="Enter street address"
                    data-testid="edit-parent-address"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-city">City</Label>
                    <Input
                      id="edit-city"
                      value={editData.city}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      placeholder="City"
                      data-testid="edit-parent-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-state">State</Label>
                    <Input
                      id="edit-state"
                      value={editData.state}
                      onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                      placeholder="State"
                      data-testid="edit-parent-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-zip">ZIP Code</Label>
                    <Input
                      id="edit-zip"
                      value={editData.zip}
                      onChange={(e) => setEditData({ ...editData, zip: e.target.value })}
                      placeholder="ZIP"
                      data-testid="edit-parent-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-500" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-emergency-name">Contact Name</Label>
                    <Input
                      id="edit-emergency-name"
                      value={editData.emergency_contact_name}
                      onChange={(e) => setEditData({ ...editData, emergency_contact_name: e.target.value })}
                      placeholder="Emergency contact name"
                      data-testid="edit-parent-emergency-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-emergency-phone">Contact Phone</Label>
                    <Input
                      id="edit-emergency-phone"
                      value={editData.emergency_contact_phone}
                      onChange={(e) => setEditData({ ...editData, emergency_contact_phone: e.target.value })}
                      placeholder="Emergency contact phone"
                      data-testid="edit-parent-emergency-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Admin Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Internal notes about this parent..."
                  rows={3}
                  data-testid="edit-parent-notes"
                />
              </div>

              <DialogFooter className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  data-testid="save-parent-btn"
                >
                  {saving ? (
                    <span className="animate-spin mr-1">⏳</span>
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Status Update Dialog */}
        <Dialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-500" />
                Update Parent Status
              </DialogTitle>
              <DialogDescription>
                Update the status for {selectedParentIds.length} selected parent{selectedParentIds.length !== 1 ? 's' : ''}.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger data-testid="bulk-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="inactive">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500" />
                        Inactive
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {bulkStatus === 'inactive' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Setting parents to inactive will prevent them from logging in. Parents with active enrollments cannot be set to inactive.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkStatusDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkStatusUpdate}
                disabled={bulkProcessing}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="confirm-bulk-status-btn"
              >
                {bulkProcessing ? 'Updating...' : `Update ${selectedParentIds.length} Parent${selectedParentIds.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Email Dialog */}
        <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-500" />
                Send Bulk Email
              </DialogTitle>
              <DialogDescription>
                Send an email to {selectedParentIds.length} selected parent{selectedParentIds.length !== 1 ? 's' : ''}.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Template</Label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger data-testid="bulk-email-template-select">
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplateId && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-1">Preview:</p>
                  <p className="text-sm text-slate-600">
                    {emailTemplates.find(t => t.id === selectedTemplateId)?.subject || 'No subject'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkEmailDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkSendEmail}
                disabled={bulkProcessing || !selectedTemplateId}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="confirm-bulk-email-btn"
              >
                {bulkProcessing ? 'Sending...' : `Send to ${selectedParentIds.length} Parent${selectedParentIds.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Parents;
